import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import type { ChatKnowledgeBase } from '@limo/database'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useToast } from '@/hooks/use-toast'
import { Plus, Search, Brain, MoreHorizontal, Pencil, Trash2, ArrowUp, ArrowDown } from 'lucide-react'
import { KnowledgeBaseDialog } from '@/components/chat/KnowledgeBaseDialog'

export function KnowledgeBasePage() {
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  // State
  const [search, setSearch] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; question: string } | null>(null)

  const activeAccount = isImpersonating ? impersonatedAccount : account

  // Fetch knowledge base entries
  const { data: entries, isLoading } = useQuery({
    queryKey: ['knowledge-base', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return []
      const { data, error } = await supabase
        .from('chat_knowledge_base')
        .select('*')
        .eq('account_id', activeAccount.id)
        .order('display_order', { ascending: true })
      if (error) throw error
      return data as ChatKnowledgeBase[]
    },
    enabled: !!activeAccount?.id,
  })

  // Toggle active status mutation
  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { error } = await supabase
        .from('chat_knowledge_base')
        .update({ is_active: isActive })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-base'] })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('chat_knowledge_base')
        .delete()
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-base'] })
      toast({
        title: 'Entry deleted',
        description: 'The knowledge base entry has been deleted.',
      })
      setDeleteTarget(null)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  // Move entry mutation
  const moveEntryMutation = useMutation({
    mutationFn: async ({ id, direction }: { id: string; direction: 'up' | 'down' }) => {
      if (!entries) return

      const currentIndex = entries.findIndex((e) => e.id === id)
      if (currentIndex === -1) return
      if (direction === 'up' && currentIndex === 0) return
      if (direction === 'down' && currentIndex === entries.length - 1) return

      const swapIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1
      const currentEntry = entries[currentIndex]
      const swapEntry = entries[swapIndex]

      // Swap display_order values
      const updates = [
        supabase
          .from('chat_knowledge_base')
          .update({ display_order: swapEntry.display_order })
          .eq('id', currentEntry.id),
        supabase
          .from('chat_knowledge_base')
          .update({ display_order: currentEntry.display_order })
          .eq('id', swapEntry.id),
      ]

      await Promise.all(updates)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-base'] })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  // Filter entries by search
  const filteredEntries = entries?.filter(
    (entry) =>
      entry.question.toLowerCase().includes(search.toLowerCase()) ||
      entry.answer.toLowerCase().includes(search.toLowerCase()) ||
      (entry.category && entry.category.toLowerCase().includes(search.toLowerCase()))
  )

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text
    return text.slice(0, maxLength) + '...'
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Knowledge Base</h1>
          <p className="text-muted-foreground">
            Custom Q&A pairs to help the AI answer customer questions
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="w-full sm:w-auto">
          <Plus className="mr-2 h-4 w-4" />
          Add Entry
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search questions, answers, or categories..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : !entries || entries.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Brain className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No knowledge base entries</h3>
          <p className="text-muted-foreground mb-4 max-w-md">
            Add custom Q&A pairs to help the AI assistant answer specific questions
            about your business that aren't covered by FAQs.
          </p>
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add First Entry
          </Button>
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40%]">Question</TableHead>
                <TableHead className="w-[25%] hidden md:table-cell">Category</TableHead>
                <TableHead className="w-[15%] hidden sm:table-cell">Active</TableHead>
                <TableHead className="w-[20%] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEntries?.map((entry, index) => (
                <TableRow key={entry.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{truncateText(entry.question, 60)}</p>
                      <p className="text-sm text-muted-foreground">
                        {truncateText(entry.answer, 80)}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {entry.category ? (
                      <Badge variant="secondary">{entry.category}</Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <Switch
                      checked={entry.is_active}
                      onCheckedChange={(checked) =>
                        toggleActiveMutation.mutate({ id: entry.id, isActive: checked })
                      }
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          moveEntryMutation.mutate({ id: entry.id, direction: 'up' })
                        }
                        disabled={index === 0}
                        className="hidden sm:inline-flex"
                      >
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          moveEntryMutation.mutate({ id: entry.id, direction: 'down' })
                        }
                        disabled={index === (filteredEntries?.length || 0) - 1}
                        className="hidden sm:inline-flex"
                      >
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setEditId(entry.id)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              setDeleteTarget({ id: entry.id, question: entry.question })
                            }
                            className="text-destructive"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete knowledge base entry?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{truncateText(deleteTarget?.question || '', 50)}".
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add/Edit dialog */}
      {activeAccount && (
        <>
          <KnowledgeBaseDialog
            entryId={null}
            accountId={activeAccount.id}
            open={dialogOpen}
            onOpenChange={setDialogOpen}
          />
          <KnowledgeBaseDialog
            entryId={editId}
            accountId={activeAccount.id}
            open={!!editId}
            onOpenChange={(open) => !open && setEditId(null)}
          />
        </>
      )}
    </div>
  )
}
